<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { api, ApiError } from '../api/http'
import { useI18n } from '../i18n/t'
import { useAuthStore } from '../stores/auth'

type ForbiddenVote = 'forbidden' | 'limited'

type PollState = {
  forbidden: number
  limited: number
  vote: ForbiddenVote | null
  comment: string
}

const { t } = useI18n()
const auth = useAuthStore()

const forbidden = ref(0)
const limited = ref(0)
const vote = ref<ForbiddenVote | null>(null)
const draft = ref('')
const savedComment = ref('')
const voteBusy = ref(false)
const commentBusy = ref(false)
const voteNote = ref<'saved' | ''>('')
const commentNote = ref<'saved' | ''>('')
const error = ref<'rate' | 'fail' | ''>('')
const loadError = ref(false)
const ready = ref(false)
const open = ref(true)

const voted = computed(() => vote.value !== null)
const voteLabel = computed(() =>
  vote.value === 'limited' ? t('home.banPollLimited') : t('home.banPollForbidden'),
)
const total = computed(() => forbidden.value + limited.value)

function share(count: number): number {
  if (total.value === 0) return 0
  return Math.round((count / total.value) * 100)
}

const forbiddenShare = computed(() => share(forbidden.value))
const limitedShare = computed(() => share(limited.value))
const forbiddenWidth = computed(() => `${forbiddenShare.value}%`)
const limitedWidth = computed(() => `${limitedShare.value}%`)
const canSendComment = computed(
  () =>
    !commentBusy.value &&
    draft.value.trim().length >= 2 &&
    draft.value.trim() !== savedComment.value,
)

function toggle(): void {
  open.value = !open.value
}

function apply(state: PollState): void {
  const previous = savedComment.value
  forbidden.value = state.forbidden
  limited.value = state.limited
  vote.value = state.vote
  savedComment.value = state.comment
  if (draft.value === previous) draft.value = state.comment
}

onMounted(async () => {
  try {
    apply(await api<PollState>('/poll/forbidden-cards', { token: auth.token }))
    if (vote.value !== null) open.value = false
  } catch {
    loadError.value = true
  } finally {
    ready.value = true
  }
})

async function choose(next: ForbiddenVote): Promise<void> {
  if (voteBusy.value || vote.value === next) return
  voteBusy.value = true
  voteNote.value = ''
  error.value = ''
  try {
    apply(
      await api<PollState>('/poll/forbidden-cards', {
        method: 'POST',
        token: auth.token,
        body: JSON.stringify({ vote: next }),
      }),
    )
    voteNote.value = 'saved'
    loadError.value = false
  } catch (err) {
    error.value = err instanceof ApiError && err.code === 'rate_limited' ? 'rate' : 'fail'
  } finally {
    voteBusy.value = false
  }
}

async function sendComment(): Promise<void> {
  if (!canSendComment.value) return
  commentBusy.value = true
  commentNote.value = ''
  error.value = ''
  try {
    const state = await api<PollState>('/poll/forbidden-cards', {
      method: 'POST',
      token: auth.token,
      body: JSON.stringify({ comment: draft.value }),
    })
    apply(state)
    draft.value = state.comment
    commentNote.value = 'saved'
    loadError.value = false
    if (vote.value !== null) open.value = false
  } catch (err) {
    error.value = err instanceof ApiError && err.code === 'rate_limited' ? 'rate' : 'fail'
  } finally {
    commentBusy.value = false
  }
}
</script>

<template>
  <section
    v-show="ready"
    class="ban-poll"
    :class="{ slim: !open }"
    aria-labelledby="ban-poll-title"
  >
    <button
      type="button"
      class="fold"
      :aria-expanded="open"
      @click="toggle"
    >
      <span class="fold-copy">
        <span id="ban-poll-title" class="fold-title">{{ t('home.banPollTitle') }}</span>
        <span v-if="voted" class="fold-status">{{ t('home.pollFolded', { vote: voteLabel }) }}</span>
        <span
          v-if="!open && total > 0"
          class="fold-bar"
          aria-hidden="true"
        >
          <span class="forbidden" :style="{ width: forbiddenWidth }" />
          <span class="limited" :style="{ width: limitedWidth }" />
        </span>
      </span>
      <svg
        class="chevron"
        :class="{ open }"
        viewBox="0 0 24 24"
        width="24"
        height="24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        aria-hidden="true"
      >
        <path d="M9 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </button>
    <div v-show="open">
      <p class="lead">{{ t('home.banPollLead') }}</p>
      <fieldset class="choices">
        <legend class="visually-hidden">{{ t('home.banPollTitle') }}</legend>
        <button
          type="button"
          class="choice"
          :class="{ on: vote === 'forbidden' }"
          :aria-pressed="vote === 'forbidden'"
          :disabled="voteBusy"
          @click="choose('forbidden')"
        >
          {{ t('home.banPollForbidden') }}
        </button>
        <button
          type="button"
          class="choice"
          :class="{ on: vote === 'limited' }"
          :aria-pressed="vote === 'limited'"
          :disabled="voteBusy"
          @click="choose('limited')"
        >
          {{ t('home.banPollLimited') }}
        </button>
      </fieldset>

      <div
        class="results"
        role="group"
        :aria-label="t('home.banPollResults')"
      >
        <div class="result">
          <div class="result-head">
            <span>{{ t('home.banPollForbidden') }}</span>
            <span>{{ t('home.banPollCount', { count: forbidden, pct: forbiddenShare }) }}</span>
          </div>
          <div
            class="track"
            role="meter"
            :aria-valuenow="forbidden"
            aria-valuemin="0"
            :aria-valuemax="Math.max(total, 1)"
            :aria-label="t('home.banPollForbidden')"
          >
            <span class="fill forbidden" :style="{ width: forbiddenWidth }" />
          </div>
        </div>
        <div class="result">
          <div class="result-head">
            <span>{{ t('home.banPollLimited') }}</span>
            <span>{{ t('home.banPollCount', { count: limited, pct: limitedShare }) }}</span>
          </div>
          <div
            class="track"
            role="meter"
            :aria-valuenow="limited"
            aria-valuemin="0"
            :aria-valuemax="Math.max(total, 1)"
            :aria-label="t('home.banPollLimited')"
          >
            <span class="fill limited" :style="{ width: limitedWidth }" />
          </div>
        </div>
      </div>
      <p v-if="total === 0" class="tally">{{ t('home.banPollEmpty') }}</p>
      <p v-else class="tally">{{ t('home.banPollTally', { forbidden, limited }) }}</p>
      <p v-if="voteNote === 'saved'" class="note ok">{{ t('home.pollSaved') }}</p>

      <form class="comment" @submit.prevent="sendComment">
        <label class="field">
          <span class="field-label">{{ t('home.banPollCommentTitle') }}</span>
          <span class="lead">{{ t('home.banPollCommentLead') }}</span>
          <textarea
            v-model="draft"
            rows="4"
            maxlength="500"
            :placeholder="t('home.banPollCommentPlaceholder')"
            :disabled="commentBusy"
          />
        </label>
        <p v-if="commentNote === 'saved'" class="note ok">{{ t('home.banPollCommentThanks') }}</p>
        <button type="submit" class="send" :disabled="!canSendComment">
          {{ t('home.banPollCommentSend') }}
        </button>
      </form>
      <p v-if="loadError" class="note err">{{ t('home.pollLoadError') }}</p>
      <p v-if="error === 'rate'" class="note err">{{ t('home.pollRate') }}</p>
      <p v-else-if="error === 'fail'" class="note err">{{ t('home.pollError') }}</p>
    </div>
  </section>
</template>

<style scoped>
.ban-poll {
  margin: 0 0 24px;
  padding: 16px;
  border-radius: 12px;
  background: var(--md-sys-color-surface-container);
  box-shadow: var(--md-elevation-1);
}

.ban-poll.slim {
  padding: 8px 16px;
}

.fold-title {
  margin: 0;
  font-size: 22px;
  line-height: 28px;
  font-weight: 400;
  color: var(--md-sys-color-on-surface);
}

.fold {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin: 0;
  padding: 4px 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

/* The global press scale shrinks this full-width row. A click on the
   chevron then lands outside the button and never fires. */
button.fold:not(:disabled):not(.disabled):active {
  scale: 1;
}

.ban-poll:not(.slim) .fold {
  margin-bottom: 12px;
}

.fold-copy {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
  gap: 4px;
}

.fold-title {
  font-size: 16px;
  line-height: 24px;
}

.fold-status {
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
  line-height: 20px;
}

.fold-bar,
.track {
  display: flex;
  height: 8px;
  overflow: hidden;
  border-radius: 999px;
  background: var(--md-sys-color-surface-container-high);
}

.fold-bar {
  width: 100%;
  margin-top: 4px;
}

.chevron {
  flex-shrink: 0;
  color: var(--md-sys-color-primary);
  transition: transform var(--md-motion-short) var(--md-motion-standard);
}

.chevron.open {
  transform: rotate(90deg);
}

.lead {
  margin: 0 0 12px;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
  line-height: 20px;
}

.choices {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0;
  padding: 0;
  border: 0;
  min-inline-size: 0;
}

.choice,
.send {
  min-height: 40px;
  padding: 0 16px;
  border: 0;
  border-radius: 20px;
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.1px;
  cursor: pointer;
}

.choice {
  background: var(--md-sys-color-surface-container-high);
  color: var(--md-sys-color-on-surface);
}

.choice.on {
  background: var(--md-sys-color-primary-container);
  color: var(--md-sys-color-on-primary-container);
}

.choice:disabled,
.send:disabled {
  opacity: 0.5;
  cursor: default;
}

.results {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
}

.result-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;
  color: var(--md-sys-color-on-surface);
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
}

.track {
  height: 12px;
}

.fill,
.fold-bar .forbidden,
.fold-bar .limited {
  display: block;
  height: 100%;
  transition: width var(--md-motion-medium) var(--md-motion-standard);
}

.fill.forbidden,
.fold-bar .forbidden {
  background: var(--md-sys-color-primary);
}

.fill.limited,
.fold-bar .limited {
  background: var(--md-sys-color-on-surface-variant);
}

.tally {
  margin: 12px 0 0;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
  line-height: 20px;
}

.comment {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--md-sys-color-outline-variant);
}

.field {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.field-label {
  margin-bottom: 4px;
  color: var(--md-sys-color-on-surface);
  font-size: 16px;
  line-height: 24px;
}

textarea {
  width: 100%;
  min-height: 96px;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid var(--md-sys-color-outline);
  background: var(--md-sys-color-surface);
  color: var(--md-sys-color-on-surface);
  font: inherit;
  font-size: 14px;
  line-height: 20px;
  resize: vertical;
}

textarea:focus {
  outline: 2px solid var(--md-sys-color-primary);
  outline-offset: 1px;
}

.send {
  background: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
}

.note {
  margin: 8px 0 0;
  font-size: 14px;
  line-height: 20px;
}

.note.ok {
  color: var(--md-sys-color-primary);
}

.note.err {
  color: var(--md-sys-color-error);
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
}

@media (prefers-reduced-motion: reduce) {
  .chevron,
  .fill,
  .fold-bar .forbidden,
  .fold-bar .limited {
    transition: none;
  }
}

@media (max-height: 500px) and (max-width: 1100px) {
  .ban-poll {
    margin-bottom: 12px;
    padding: 10px 12px;
  }

  .lead,
  .tally,
  .note,
  .result-head,
  textarea {
    font-size: 12px;
    line-height: 16px;
  }
}
</style>
